"use client"

import {
  CheckCircle2,
  Database,
  Globe,
  Hourglass,
  Keyboard,
  List,
  Phone,
  Plus,
  QrCode,
  Settings2,
  Signal,
  Smartphone,
  Tag,
  Wifi,
  type LucideIcon,
} from "lucide-react"

import { accountText, type LocaleProps } from "../../personal-web"
import type { Step, StepIconName } from "../data/steps"

/**
 * Maps the data-layer icon name (a string in steps.ts) to its Lucide
 * component. Adding a new StepIconName requires extending both this map
 * and the union in data/steps.ts — TypeScript will yell at us until both
 * sides match.
 */
const ICON_MAP: Record<StepIconName, LucideIcon> = {
  settings: Settings2,
  smartphone: Smartphone,
  qr: QrCode,
  keyboard: Keyboard,
  check: CheckCircle2,
  hourglass: Hourglass,
  tag: Tag,
  database: Database,
  phone: Phone,
  globe: Globe,
  plus: Plus,
  list: List,
  signal: Signal,
  wifi: Wifi,
}

type Props = LocaleProps & {
  steps: Step[]
  /**
   * Optional aria-label for the list (e.g. "iOS install steps"). When the
   * list lives directly under a SectionHeader, screen readers already pick
   * up the context — pass a label only when the list is decoupled.
   */
  ariaLabel?: string
}

/**
 * Renders a Step[] from data/steps.ts as the timeline-style numbered list
 * we've used since the original install page. Visual is intentionally
 * close to the legacy TimelineStep so users coming back from the old
 * design don't have to relearn anything.
 */
export function StepList({ lng, steps, ariaLabel }: Props) {
  return (
    <section
      className="relative pl-[18px] md:pl-[22px]"
      aria-label={ariaLabel}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute left-[8px] top-2 bottom-2 w-[2px] rounded-full bg-gradient-to-b from-orange-300 via-orange-200 to-stone-200 md:left-[10px]"
      />
      <ol className="grid gap-3">
        {steps.map((step, idx) => {
          const Icon = ICON_MAP[step.icon]
          return (
            <li key={step.id} className="relative">
              <span
                aria-hidden
                className="absolute -left-[18px] top-3 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white bg-orange-500 text-[10px] font-bold text-white shadow-[0_0_0_2px_rgba(249,115,22,0.25)] md:-left-[22px] md:h-5 md:w-5 md:text-[11px]"
              >
                {idx + 1}
              </span>
              <div className="ml-3 rounded-2xl border border-stone-200 bg-white p-4 transition-colors hover:bg-stone-50 md:ml-4 md:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <h4 className="text-[15px] font-bold leading-snug text-slate-950 md:text-base">
                        {accountText(lng, step.titleKey)}
                      </h4>
                      {step.badge === "optional" ? (
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
                          {accountText(lng, "install.step.badgeOptional")}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600">
                      {accountText(lng, step.bodyKey)}
                    </p>
                  </div>
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600"
                    aria-hidden
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
